import { api } from "../api";
import { ToolDialog } from "../../../../packages/test-ui/tool-dialog";
import { Link, navigate, useQueryState } from "../navigation";
import { useState } from "react";
import { ArrowRight, BookOpen, Check, Plus } from "lucide-react";
import type { LibrarySummary } from "../../../../packages/published-content/library";
import { AssignDialog } from "../AssignDialog";
import { AssignmentDueDialog } from "./AssignmentDueDialog";
import { PageHeading, RequestState } from "../PortalShell";
import { useResource, useDebouncedValue, displayTitle, statusLabel, formatDueDateTime, type Assignment } from "../portal-data";
import { SearchTests } from "./test-list";

export function AdminAssignments({ onResults }: { onResults: (id: string) => void }) {

  const library = useResource<{ release: LibrarySummary }>("/api/library");
  const [assign, setAssign] = useState<{ examId?: string; email?: string } | null>(() => new URLSearchParams(window.location.search).get("new") === "1" ? {} : null);
  const [schedule, setSchedule] = useState<Assignment | null>(null);
  const [filter, setFilter] = useQueryState("status", "all");
  const [dueFilter, setDueFilter] = useQueryState("due", "all");
  const [search, setSearch] = useQueryState("search", "");
  const settledSearch = useDebouncedValue(search);
  const [cursors, setCursors] = useQueryState<string[]>("pages", [""]);
  const assignments = useResource<{ assignments: Assignment[]; nextCursor: string | null }>(`/api/assignments?${new URLSearchParams({ compact: "true", status: filter, due: dueFilter, search: settledSearch, cursor: cursors.at(-1)! })}`);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDue, setBulkDue] = useState("");
  const [removing, setRemoving] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [message, setMessage] = useState("");
  const rows = assignments.data?.assignments ?? [];
  const filtered = rows;
  const editable = rows.filter(row => ["assigned", "in_progress"].includes(row.status));
  const selectedRows = editable.filter(row => selected.has(row.id));
  async function bulk(action: "due" | "cancel", clearDate = false) {
    if (bulkBusy || !selectedRows.length) return;
    setBulkBusy(true); setBulkError("");
    try {
      await api("/api/admin/assignments/batch", { method: "PATCH", body: JSON.stringify(action === "cancel"
        ? { action, assignmentIds: selectedRows.map(row => row.id) }
        : { action, dueAt: clearDate ? null : new Date(`${bulkDue}T23:59:59`).toISOString(), assignments: selectedRows.map(row => ({ id: row.id, expectedDueAt: row.dueAt })) }) });
      setMessage(`${selectedRows.length} assignments ${action === "cancel" ? "removed" : "updated"}.`); setSelected(new Set()); setRemoving(false); assignments.reload();
    } catch (reason) { setBulkError((reason as Error).message); }
    finally { setBulkBusy(false); }
  }
  const hasFilters = !!search.trim() || filter !== "all" || dueFilter !== "all";

  return (
    <>
      <PageHeading eyebrow="Student work" title="Assignments">
        <button className="primary-button" disabled={!library.data?.release.exams.length} onClick={() => setAssign({})}>
          <Plus size={15} />Assign tests
        </button>
      </PageHeading>
      <div className="portal-content">
        <div className="page-toolbar">
          <SearchTests search={search} onSearch={(value) => { setSearch(value); setCursors([""]); setSelected(new Set()); }} placeholder="Search student or test" />
          <span className="toolbar-note">{rows.length} assignments on this page</span>
          <select aria-label="Assignment status" value={filter} onChange={(event) => { setFilter(event.target.value); setCursors([""]); setSelected(new Set()); }}>
            {["all", "assigned", "in_progress", "completed", "cancelled"].map((status) => (
              <option key={status} value={status}>
                {status === "all" ? "All statuses" : statusLabel(status)}
              </option>
            ))}
          </select>
          <select aria-label="Due date and review filter" value={dueFilter} onChange={(event) => { setDueFilter(event.target.value); setCursors([""]); setSelected(new Set()); }}>
            <option value="all">All dates</option>
            <option value="due_soon">Due in 7 days</option>
            <option value="overdue">Overdue</option>
            <option value="needs_review">Needs review</option>
          </select>
        </div>
        <RequestState loading={assignments.loading} error={assignments.error} retry={assignments.reload} />
        <RequestState loading={false} error={library.error} retry={library.reload} />
        {message && (
          <p className="success-message" role="status">
            <Check size={16} />{message}
          </p>
        )}
        {bulkError && <p className="error" role="alert">{bulkError}</p>}
        {selectedRows.length > 0 && <div className="assignment-bulk-bar"><strong>{selectedRows.length} selected on this page</strong><label>Due date<input type="date" value={bulkDue} disabled={bulkBusy} onChange={event => setBulkDue(event.target.value)} /></label><button className="secondary-button" disabled={bulkBusy || !bulkDue} onClick={() => void bulk("due")}>Set due date</button><button className="text-button" disabled={bulkBusy} onClick={() => void bulk("due", true)}>Clear due dates</button><button className="text-button danger-text" disabled={bulkBusy} onClick={() => { setBulkError(""); setRemoving(true); }}>Remove selected</button><button className="text-button" disabled={bulkBusy} onClick={() => setSelected(new Set())}>Deselect</button></div>}
        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th><input className="assignment-check" type="checkbox" aria-label="Select active assignments on this page" disabled={bulkBusy || !editable.length} checked={editable.length > 0 && selectedRows.length === editable.length} onChange={event => setSelected(new Set(event.target.checked ? editable.map(row => row.id) : []))} /></th>
                <th>Student</th>
                <th>Test</th>
                <th>Status</th>
                <th>Due</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const statusTone = item.status === "completed" ? "good" : item.status === "in_progress" ? "alert" : "accent";
                return (
                  <tr key={item.id}>
                    <td data-label="Select"><input className="assignment-check" type="checkbox" aria-label={`Select ${displayTitle(item.examTitle)} for ${item.learnerName}`} disabled={bulkBusy || !["assigned", "in_progress"].includes(item.status)} checked={selected.has(item.id)} onChange={event => setSelected(current => { const next = new Set(current); event.target.checked ? next.add(item.id) : next.delete(item.id); return next; })} /></td>
                    <td data-label="Student">
                      <Link href={`/admin/students/${encodeURIComponent(item.learnerId)}`}>{item.learnerName}</Link>
                      <small>{item.learnerEmail}</small>
                    </td>
                    <td data-label="Test">
                      <Link href={`/admin/tests/${encodeURIComponent(item.examId)}`}>{displayTitle(item.examTitle)}</Link>
                      <small>{item.timing === "timed" ? "Timed modules" : "Untimed practice"}</small>
                    </td>
                    <td data-label="Status">
                      <span className={`status-badge ${statusTone}`}>
                        {statusLabel(item.status)}
                      </span>
                    </td>
                    <td data-label="Due">{item.dueAt ? formatDueDateTime(item.dueAt) : "—"}</td>
                    <td data-label="Actions">
                      {item.attemptId && item.attemptStatus === "completed" ? (
                        <div className="assignment-actions"><button className="text-button" onClick={() => onResults(item.attemptId!)}>
                          View results <ArrowRight size={13} />
                        </button><button className="text-button" disabled={!library.data?.release.exams.some((exam) => exam.id === item.examId)} onClick={() => setAssign({ examId: item.examId, email: item.learnerEmail })}>Assign again</button></div>
                      ) : (
                        <div className="assignment-actions">{["assigned", "in_progress"].includes(item.status) && <button className="text-button" onClick={() => setSchedule(item)}>Due date</button>}<span className="muted">{item.status === "in_progress" ? "In progress" : ""}</span></div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(cursors.length > 1 || assignments.data?.nextCursor) && <div className="list-pagination"><button className="secondary-button" disabled={cursors.length === 1} onClick={() => setCursors((items) => items.slice(0, -1))}>Previous</button><button className="secondary-button" disabled={!assignments.data?.nextCursor} onClick={() => setCursors((items) => [...items, assignments.data!.nextCursor!])}>Next</button></div>}
        {assignments.data && !filtered.length && (
          <div className="portal-empty">
            <BookOpen size={24} />
            <h2>{hasFilters ? "No matching assignments" : "No assignments yet"}</h2>
            <p>{hasFilters ? "Try another student, status, or due date." : "Choose a test and a student to create an assignment."}</p>{hasFilters && <button className="secondary-button" onClick={() => { setSearch(""); setFilter("all"); setDueFilter("all"); setCursors([""]); setSelected(new Set()); }}>Clear filters</button>}
          </div>
        )}
      </div>
      {assign && library.data && (
        <AssignDialog
          exams={library.data.release.exams}
          initialExamId={assign.examId}
          initialStudentEmail={assign.email}
          onClose={() => { setAssign(null); if (new URLSearchParams(window.location.search).get("new")) navigate("/admin/assignments", true); }}
          onAssigned={() => { setAssign(null); setMessage("Assignments saved. Existing active assignments were kept."); assignments.reload(); window.dispatchEvent(new Event("sat:portal-invalidated")); if (new URLSearchParams(window.location.search).get("new")) navigate("/admin/assignments", true); }}
        />
      )}
      {removing && <ToolDialog title={`Remove ${selectedRows.length} assignments?`} className="portal-dialog" onClose={() => { if (!bulkBusy) setRemoving(false); }}><p>These tests will leave the students’ active assignments. In-progress attempts will close; saved answers and completed work stay in history.</p>{bulkError && <p className="error" role="alert">{bulkError}</p>}<div className="dialog-actions"><button className="secondary-button" disabled={bulkBusy} onClick={() => setRemoving(false)}>Keep assignments</button><button className="primary-button" disabled={bulkBusy} onClick={() => void bulk("cancel")}>{bulkBusy ? "Removing…" : "Remove assignments"}</button></div></ToolDialog>}
      {schedule && <AssignmentDueDialog assignment={schedule} onClose={() => setSchedule(null)} onSaved={() => { setSchedule(null); setMessage("Due date updated."); assignments.reload(); }} />}
    </>
  );
}
